import React from 'react';
import { ActionsProps } from './types';
import { ArcButton, ArcStack } from '@src/core/ui';
import { useNavigate, useParams } from 'react-router-dom';
import { routesConfig } from '@src/core/routes';
import { MissionIdParams } from '@src/core/entities';
import {
  useCancelMissionMutation,
  useCreateMissionTemplateFromOrderMutation,
  useGetOrderMissionQuery,
} from '@src/features/order/redux';
import { toast } from 'react-toastify';
import { useConfirm } from '@src/core/controllers';
import { trackError, toTrackedError } from '@src/core/sentry';
import SaveTemplateDialog from '../save-template-dialog';
import { ISaveTemplateForm } from '../save-template-dialog/types';

const Actions: React.FC<ActionsProps> = (props) => {
  const { isWarnings } = props;
  const navigate = useNavigate();
  const { missionId } = useParams<MissionIdParams>();
  const { data, isLoading } = useGetOrderMissionQuery(missionId);
  const [cancelMission, { isLoading: isCanceling }] = useCancelMissionMutation();
  const [createMissionTemplateFromOrder, { isLoading: isSavingTemplate }] =
    useCreateMissionTemplateFromOrderMutation();
  const { confirm } = useConfirm();
  const [templateDialogOpen, setTemplateDialogOpen] = React.useState(false);

  const [order] = data?.orders ?? [];
  const defaultTemplateName = order?.id ? `Order #${order.id}` : '';

  const onExit = () => {
    navigate(routesConfig.orders, { replace: true });
  };

  const onEdit = () => {
    if (!missionId) return;
    navigate(`${routesConfig.orders}/update-mission/${missionId}`);
  };

  const onSaveAsTemplate = () => {
    if (!data) {
      toast.error('Mission data is not loaded yet');
      return;
    }
    if (!order?.id) {
      toast.error('Order is not available');
      return;
    }
    setTemplateDialogOpen(true);
  };

  const onTemplateSubmit = ({ name }: ISaveTemplateForm) => {
    if (!order?.id) return;
    createMissionTemplateFromOrder({ orderId: order.id, name: name.trim() })
      .unwrap()
      .then(() => {
        toast.success('Mission template saved');
        setTemplateDialogOpen(false);
      })
      .catch((err: unknown) => {
        trackError(toTrackedError(err), { code: 'FE_0020', extra: { flow: 'saveMissionTemplateFromOrder' } });
        toast.error('Failed to save mission template');
      });
  };

  const onDelete = () => {
    if (!missionId) return;
    cancelMission(missionId).unwrap().then(() => {
      toast.success('Mission has been canceled');
      navigate(routesConfig.orders, { replace: true });
    }).catch((err: unknown) => {
      trackError(toTrackedError(err), { code: 'FE_0020', extra: { flow: 'confirmOrderCancelMission' } });
    });
  };

  const onContinue = () => {
    if (!missionId) return;
    if (isWarnings) {
      confirm(
        'The current weather conditions exceed safe limits for flight. Proceeding may put the drone at risk of damage or loss. Do you want to continue anyway?',
        'Unsafe Weather Conditions',
      ).then((confirmed) => {
        if (confirmed) {
          navigate(`${routesConfig.orders}/checklist/${missionId}`);
        }
      });
    } else {
      navigate(`${routesConfig.orders}/checklist/${missionId}`);
    }
  };

  if (isLoading) return null;

  return (
    <>
      <ArcStack spacing={1}>
        <ArcStack direction='row' spacing={1}>
          <ArcButton.ghost fullWidth onClick={onExit}>
            Exit
          </ArcButton.ghost>
          <ArcButton.ghost fullWidth onClick={onEdit}>
            Edit Mission
          </ArcButton.ghost>
        </ArcStack>

        <ArcStack direction='row' spacing={1}>
          <ArcButton.ghost fullWidth isLoading={isCanceling} onClick={onDelete}>
            Delete Mission
          </ArcButton.ghost>
          <ArcButton.ghost fullWidth onClick={onSaveAsTemplate}>
            Save as Template
          </ArcButton.ghost>
        </ArcStack>

        <ArcButton.brand fullWidth onClick={onContinue}>
          Next
        </ArcButton.brand>
      </ArcStack>

      <SaveTemplateDialog
        open={templateDialogOpen}
        onClose={() => setTemplateDialogOpen(false)}
        defaultName={defaultTemplateName}
        onSubmit={onTemplateSubmit}
        isSubmitting={isSavingTemplate}
      />
    </>
  );
};

export default Actions;
